import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Layers, ZoomIn, ZoomOut, Maximize2, Building2, MapPin, Triangle, Home, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const NodeIcon = ({ type, status, size = 'md' }) => {
  const statusColor = 
    status === 'good' ? 'text-success' : 
    status === 'warning' ? 'text-warning' : 'text-destructive';
  
  const bgColor = 
    status === 'good' ? 'bg-success/10 border-success' : 
    status === 'warning' ? 'bg-warning/10 border-warning' : 'bg-destructive/10 border-destructive';
  
  const sizeClasses = 
    size === 'lg' ? 'w-14 h-14' :
    size === 'md' ? 'w-10 h-10' : 'w-7 h-7';
  
  const iconSize = 
    size === 'lg' ? 'w-7 h-7' :
    size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  
  if (type === 'wtp') {
    return (
      <div className={`${sizeClasses} rounded-lg border-2 ${bgColor} flex items-center justify-center shadow-md`}>
        <Building2 className={`${iconSize} ${statusColor}`} />
      </div>
    );
  }
  
  if (type === 'oht') {
    return (
      <div className={`${sizeClasses} rounded-full border-2 ${bgColor} flex items-center justify-center shadow-md`}>
        <Triangle className={`${iconSize} ${statusColor}`} />
      </div>
    );
  }
  
  if (type === 'dma') {
    return (
      <div className={`${sizeClasses} rounded border-2 ${bgColor} flex items-center justify-center shadow-md`}>
        <MapPin className={`${iconSize} ${statusColor}`} />
      </div>
    );
  }
  
  if (type === 'household') {
    return (
      <div className={`${sizeClasses} rounded-md border-2 ${bgColor} flex items-center justify-center shadow-sm`}>
        <Home className={`${iconSize} ${statusColor}`} />
      </div>
    );
  }
};

export const NetworkMap = ({ villageData = {} }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState('all');
  const [viewMode, setViewMode] = useState('hierarchy'); // 'hierarchy' or 'flow'
  const [lpcdSettings, setLpcdSettings] = useState({ target: 100, persons_per_household: 4 });
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  // Fetch LPCD settings from backend
  useEffect(() => {
    loadLPCDSettings();
  }, []);

  const loadLPCDSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/lpcd`);
      const result = await response.json();
      if (result.success && result.data) {
        setLpcdSettings({
          target: result.data.target,
          persons_per_household: result.data.persons_per_household
        });
      }
    } catch (error) {
      console.error('Error loading LPCD settings:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };
  
  const villagesArray = Object.entries(villageData).map(([key, village]) => ({
    id: key,
    ...village,
    status: village.nrw > 15 ? 'critical' : village.nrw > 12 ? 'warning' : 'good'
  }));
  
  const filteredVillages = selectedVillage === 'all' 
    ? villagesArray 
    : villagesArray.filter(v => v.id === selectedVillage);
  
  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Network Visualization</h1>
        <p className="text-muted-foreground">Interactive map of water distribution infrastructure across Salcette Block</p>
      </div>
      
      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Village Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedVillage} onValueChange={setSelectedVillage}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Villages</SelectItem>
                  {villagesArray.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <Button 
                variant={viewMode === 'hierarchy' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('hierarchy')}
              >
                Hierarchy View
              </Button>
              <Button 
                variant={viewMode === 'flow' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('flow')}
              >
                Flow View
              </Button>
            </div>
            
            {/* Legend */}
            <div className="ml-auto flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                <span className="text-xs">WTP</span>
              </div>
              <div className="flex items-center gap-2">
                <Triangle className="w-4 h-4 text-secondary" />
                <span className="text-xs">OHT</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-accent" />
                <span className="text-xs">DMA</span>
              </div>
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-success" />
                <span className="text-xs">Households</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Network Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map Canvas */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Infrastructure Network Map</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-br from-blue-50/30 to-cyan-50/30 dark:from-blue-950/10 dark:to-cyan-950/10 rounded-lg p-6 min-h-[700px] overflow-auto">
              {viewMode === 'hierarchy' ? (
                // Hierarchy View - Show all villages with their infrastructure
                <div className="space-y-8">
                  {filteredVillages.map((village, vIdx) => (
                    <div key={village.id} className="p-6 bg-card/80 backdrop-blur rounded-lg border-2 border-primary/20">
                      {/* Village Header */}
                      <div 
                        className="flex items-center justify-between mb-6 cursor-pointer hover:bg-muted/50 p-3 rounded-lg transition-colors"
                        onClick={() => setSelectedNode({ type: 'village', data: village })}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-primary-foreground" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold">{village.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {(village.households ?? 0).toLocaleString()} households • NRW: {village.nrw ?? 0}%
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={
                          village.status === 'good' ? 'bg-success/10 text-success border-success/20' :
                          village.status === 'warning' ? 'bg-warning/10 text-warning border-warning/20' :
                          'bg-destructive/10 text-destructive border-destructive/20'
                        }>
                          {village.status}
                        </Badge>
                      </div>
                      
                      {/* WTP Level */}
                      <div className="ml-6 space-y-4">
                        {[...Array(village.wtps)].map((_, wtpIdx) => (
                          <div key={wtpIdx} className="flex items-start gap-4">
                            <div 
                              className="cursor-pointer hover:scale-110 transition-transform"
                              onClick={() => setSelectedNode({ 
                                type: 'wtp', 
                                data: { 
                                  name: `${village.name} - WTP-${wtpIdx + 1}`,
                                  village: village.name,
                                  capacity: '5 MLD',
                                  output: '4.2 MLD',
                                  status: village.status
                                }
                              })}
                            >
                              <NodeIcon type="wtp" status={village.status} size="lg" />
                              <p className="text-xs text-center mt-1 font-medium">WTP-{wtpIdx + 1}</p>
                            </div>
                            
                            {/* OHT Level */}
                            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                              {[...Array(Math.min(4, Math.ceil(village.ohts / village.wtps)))].map((_, ohtIdx) => (
                                <div key={ohtIdx} className="flex flex-col items-center">
                                  <div 
                                    className="cursor-pointer hover:scale-110 transition-transform"
                                    onClick={() => setSelectedNode({ 
                                      type: 'oht',
                                      data: {
                                        name: `OHT-${wtpIdx * 4 + ohtIdx + 1}`,
                                        village: village.name,
                                        wtp: `WTP-${wtpIdx + 1}`,
                                        level: Math.floor(Math.random() * 30 + 70) + '%',
                                        capacity: '500 KL',
                                        status: Math.random() > 0.7 ? 'warning' : 'good'
                                      }
                                    })}
                                  >
                                    <NodeIcon type="oht" status={Math.random() > 0.7 ? 'warning' : 'good'} />
                                    <p className="text-xs text-center mt-1">OHT-{wtpIdx * 4 + ohtIdx + 1}</p>
                                  </div>
                                  
                                  {/* DMA Level */}
                                  <div className="mt-2 flex gap-1">
                                    {[...Array(Math.min(3, Math.ceil(village.dmas / village.ohts)))].map((_, dmaIdx) => (
                                      <div 
                                        key={dmaIdx}
                                        className="cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => setSelectedNode({ 
                                          type: 'dma',
                                          data: {
                                            name: `DMA-${(wtpIdx * 4 + ohtIdx) * 3 + dmaIdx + 1}`,
                                            village: village.name,
                                            oht: `OHT-${wtpIdx * 4 + ohtIdx + 1}`,
                                            households: Math.floor(village.households / village.dmas),
                                            supply: Math.floor(Math.random() * 500 + 500),
                                            nrw: (Math.random() * 10 + 5).toFixed(1),
                                            status: Math.random() > 0.8 ? 'critical' : village.status
                                          }
                                        })}
                                      >
                                        <NodeIcon type="dma" status={Math.random() > 0.8 ? 'critical' : village.status} size="sm" />
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Household indicator */}
                                  <div 
                                    className="mt-2 cursor-pointer hover:scale-110 transition-transform"
                                    onClick={() => setSelectedNode({ 
                                      type: 'household',
                                      data: {
                                        name: 'Household Cluster',
                                        village: village.name,
                                        count: Math.floor(village.households / village.dmas),
                                        lpcd: Math.round(lpcdSettings.target * 0.85), // ~85% of target
                                        lpcd_target: lpcdSettings.target,
                                        supply: 'Regular',
                                        status: 'good'
                                      }
                                    })}
                                  >
                                    <NodeIcon type="household" status="good" size="sm" />
                                    <p className="text-xs text-center mt-1">{Math.floor(village.households / village.dmas)} HH</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Flow View - Simplified flow diagram
                <div className="space-y-6">
                  {filteredVillages.map((village) => (
                    <div key={village.id} className="flex items-center gap-4 p-4 bg-card/80 backdrop-blur rounded-lg">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-lg bg-primary flex items-center justify-center mb-2">
                          <MapPin className="w-8 h-8 text-primary-foreground" />
                        </div>
                        <p className="text-sm font-bold">{village.name}</p>
                      </div>
                      
                      <div className="flex-1 h-1 bg-primary/30" />
                      
                      <div className="flex gap-2">
                        {[...Array(Math.min(3, village.wtps))].map((_, i) => (
                          <NodeIcon key={i} type="wtp" status={village.status} />
                        ))}
                        {village.wtps > 3 && (
                          <div className="text-xs text-muted-foreground self-center">+{village.wtps - 3}</div>
                        )}
                      </div>
                      
                      <div className="flex-1 h-1 bg-secondary/30" />
                      
                      <div className="flex gap-2">
                        {[...Array(Math.min(4, village.ohts))].map((_, i) => (
                          <NodeIcon key={i} type="oht" status="good" size="sm" />
                        ))}
                        {village.ohts > 4 && (
                          <div className="text-xs text-muted-foreground self-center">+{village.ohts - 4}</div>
                        )}
                      </div>
                      
                      <div className="flex-1 h-1 bg-accent/30" />
                      
                      <div className="flex gap-2">
                        {[...Array(Math.min(5, Math.ceil(village.dmas / 10)))].map((_, i) => (
                          <NodeIcon key={i} type="dma" status={village.status} size="sm" />
                        ))}
                        <div className="text-xs text-muted-foreground self-center">{village.dmas} DMAs</div>
                      </div>
                      
                      <div className="flex-1 h-1 bg-success/30" />
                      
                      <div className="text-center">
                        <NodeIcon type="household" status="good" size="md" />
                        <p className="text-xs mt-1">{(village.households ?? 0).toLocaleString()} HH</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Node Details Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Details Panel</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedNode ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{selectedNode.data.name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">
                      {selectedNode.type === 'village' ? 'Village' :
                       selectedNode.type === 'wtp' ? 'Water Treatment Plant' : 
                       selectedNode.type === 'oht' ? 'Overhead Tank' : 
                       selectedNode.type === 'dma' ? 'District Metered Area' :
                       'Household Cluster'}
                    </p>
                  </div>
                  {selectedNode.data.status && (
                    <Badge 
                      variant="outline"
                      className={
                        selectedNode.data.status === 'good' ? 'bg-success/10 text-success border-success/20' :
                        selectedNode.data.status === 'warning' ? 'bg-warning/10 text-warning border-warning/20' :
                        'bg-destructive/10 text-destructive border-destructive/20'
                      }
                    >
                      {selectedNode.data.status}
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-3 border-t border-border pt-4">
                  {/* Village Details */}
                  {selectedNode.type === 'village' && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-muted rounded">
                          <p className="text-xs text-muted-foreground">WTPs</p>
                          <p className="text-lg font-bold">{selectedNode.data.wtps}</p>
                        </div>
                        <div className="p-3 bg-muted rounded">
                          <p className="text-xs text-muted-foreground">OHTs</p>
                          <p className="text-lg font-bold">{selectedNode.data.ohts}</p>
                        </div>
                        <div className="p-3 bg-muted rounded">
                          <p className="text-xs text-muted-foreground">DMAs</p>
                          <p className="text-lg font-bold">{selectedNode.data.dmas}</p>
                        </div>
                        <div className="p-3 bg-muted rounded">
                          <p className="text-xs text-muted-foreground">Households</p>
                          <p className="text-lg font-bold">{(selectedNode.data.households ?? 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="p-3 bg-primary/5 rounded">
                        <p className="text-xs text-muted-foreground">NRW Level</p>
                        <p className="text-2xl font-bold text-primary">{selectedNode.data.nrw}%</p>
                      </div>
                    </>
                  )}
                  
                  {/* WTP Details */}
                  {selectedNode.type === 'wtp' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Village:</span>
                        <span className="text-sm font-medium">{selectedNode.data.village}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Capacity:</span>
                        <span className="text-sm font-medium">{selectedNode.data.capacity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Current Output:</span>
                        <span className="text-sm font-medium">{selectedNode.data.output}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Efficiency:</span>
                        <span className="text-sm font-medium text-success">84%</span>
                      </div>
                    </>
                  )}
                  
                  {/* OHT Details */}
                  {selectedNode.type === 'oht' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Village:</span>
                        <span className="text-sm font-medium">{selectedNode.data.village}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Connected to:</span>
                        <span className="text-sm font-medium">{selectedNode.data.wtp}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Storage Level:</span>
                        <span className="text-sm font-medium">{selectedNode.data.level}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Capacity:</span>
                        <span className="text-sm font-medium">{selectedNode.data.capacity}</span>
                      </div>
                    </>
                  )}
                  
                  {/* DMA Details */}
                  {selectedNode.type === 'dma' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Village:</span>
                        <span className="text-sm font-medium">{selectedNode.data.village}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Connected to:</span>
                        <span className="text-sm font-medium">{selectedNode.data.oht}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Households:</span>
                        <span className="text-sm font-medium">{selectedNode.data.households}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Supply (m³/day):</span>
                        <span className="text-sm font-medium">{selectedNode.data.supply}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">NRW:</span>
                        <span className="text-sm font-medium text-warning">{selectedNode.data.nrw}%</span>
                      </div>
                    </>
                  )}
                  
                  {/* Household Details */}
                  {selectedNode.type === 'household' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Village:</span>
                        <span className="text-sm font-medium">{selectedNode.data.village}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Households:</span>
                        <span className="text-sm font-medium">{selectedNode.data.count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">LPCD (Current):</span>
                        <span className="text-sm font-medium">{selectedNode.data.lpcd} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">LPCD (Target):</span>
                        <span className="text-sm font-medium text-primary">{selectedNode.data.lpcd_target} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Supply Status:</span>
                        <span className="text-sm font-medium text-success">{selectedNode.data.supply}</span>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="pt-4 border-t border-border">
                  <Button className="w-full" size="sm">View Detailed Analytics</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-96 text-center">
                <Layers className="w-16 h-16 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">Click on any infrastructure node to view details</p>
                <p className="text-xs text-muted-foreground mt-2">Villages • WTPs • OHTs • DMAs • Households</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{villagesArray.length}</p>
            <p className="text-xs text-muted-foreground">Villages</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">
              {villagesArray.reduce((sum, v) => sum + v.wtps, 0)}
            </p>
            <p className="text-xs text-muted-foreground">WTPs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-secondary">
              {villagesArray.reduce((sum, v) => sum + v.ohts, 0)}
            </p>
            <p className="text-xs text-muted-foreground">OHTs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-accent">
              {villagesArray.reduce((sum, v) => sum + v.dmas, 0)}
            </p>
            <p className="text-xs text-muted-foreground">DMAs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-success">
              {villagesArray.reduce((sum, v) => sum + (v.households ?? 0), 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Households</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NetworkMap;